import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper, Dialog, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import ListeningTestUI from '../components/IELTS/ListeningTestUI';
import TestModeModal from '../components/IELTS/TestModeModal';
import { roundIELTSBandScore } from '../components/IELTS/utils/scoreHelpers';

const PageContainer = styled(Container)(({ theme }) => ({
  padding: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const InstructionsContainer = styled(Paper)(({ theme }) => ({
  padding: '24px',
  marginBottom: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
}));

const StartButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  padding: '10px 24px',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
}));

const ResultDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
  },
}));

const ListeningTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testStarted, setTestStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [modeModalOpen, setModeModalOpen] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null); // 'practice' or 'simulation'
  const [selectedSectionCount, setSelectedSectionCount] = useState(4); // default to 4 for simulation
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(30); // default to 30 for simulation (IELTS Listening)
  const [selectedSectionIndices, setSelectedSectionIndices] = useState([0,1,2,3]); // default to all
  
  const testDuration = selectedTimeLimit; // Use selected time limit
  const testData = location.state?.testData;
  
  // Handle modal callbacks
  const handleStartPracticeMode = (selectedParts = [1,2,3,4], timeLimit = 30) => {
    setSelectedMode('practice');
    setSelectedSectionIndices(selectedParts.map(p => p - 1)); // convert to 0-based indices
    setSelectedSectionCount(selectedParts.length);
    setSelectedTimeLimit(timeLimit);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  const handleStartSimulationMode = () => {
    setSelectedMode('simulation');
    setSelectedSectionCount(4);
    setSelectedTimeLimit(30);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  
  const handleTestComplete = (results) => {
    // Calculate score
    let correctAnswers = 0;
    results.forEach(question => {
      if (question.answer.trim().toLowerCase() === question.correctAnswer.toLowerCase()) {
        correctAnswers++;
      }
    });
    
    const score = results.length ? roundIELTSBandScore((correctAnswers / results.length) * 9) : 0; // IELTS scores are on a scale of 0-9
    
    setTestResults({
      totalQuestions: results.length,
      correctAnswers,
      score
    });
    
    setShowResults(true);
  };
  
  const handleCloseResults = () => {
    setShowResults(false);
    navigate('/dashboard');
  };
  
  if (!testStarted) {
    return (
      <>
        <TestModeModal
          open={modeModalOpen}
          onClose={() => {
            setModeModalOpen(false);
            navigate('/ielts-dashboard');
          }}
          onStartPracticeMode={handleStartPracticeMode}
          onStartSimulationMode={handleStartSimulationMode}
          testCategoryName="Listening"
        />
      </>
    );
  }
  
  if (!testData) {
    return <Typography variant="h6" color="error">No test data found. Please return to the dashboard and try again.</Typography>;
  }
  
  return (
    <>
      <ListeningTestUI 
        mode={selectedMode}
        timeLimit={testDuration} 
        onComplete={handleTestComplete} 
        sections={selectedSectionIndices.map(i => testData.sections[i]).filter(Boolean)}
        selectedSectionIndices={selectedSectionIndices}
        audioUrl={testData.audio_url}
        testId={testData.id}
      />
      
      <ResultDialog open={showResults} onClose={handleCloseResults}>
        <DialogContent>
          <Typography variant="h5" sx={{ color: '#996CFE', mb: 3, textAlign: 'center' }}>
            Test Results
          </Typography>
          
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3, color: '#333', fontWeight: 'bold' }}>
            {testResults?.score.toFixed(1)}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            You answered {testResults?.correctAnswers} out of {testResults?.totalQuestions} questions correctly.
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
            This is a practice test. Your score is an estimate based on the number of correct answers.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleCloseResults}
            sx={{ 
              backgroundColor: '#996CFE', 
              color: 'white',
              '&:hover': { backgroundColor: '#7C4DFF' } 
            }}
          >
            Return to Dashboard
          </Button>
        </DialogActions>
      </ResultDialog>
    </>
  );
};

export default ListeningTestPage;
